import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLElement} orderLimitMessage
 * @property {HTMLButtonElement[]} quantityButtons
 * @property {HTMLElement} quantityScreenReader
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLButtonElement && event.target.disabled) return;
    
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange(event);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLButtonElement && event.target.disabled) return;
    
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange(event);
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange(event);
  }

  /**
   * Handles the quantity change event.
   * @param {Event} event - The event.
   */
  // #onQuantityChange(event) {
  //   const { quantityInput, quantityScreenReader } = this.refs;

  //   this.#checkQuantityRules();
  //   const newValue = parseInt(quantityInput.value);

  //   if (quantityScreenReader) {
  //     quantityScreenReader.textContent = `Current quantity: ${quantityInput.value}`;
  //   }

  //   quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  // }

  #onQuantityChange(event) {
    const { quantityInput, quantityScreenReader } = this.refs;

    this.#checkQuantityRules();

    const parsed = parseInt(quantityInput.value, 10);
    const fallback = parseInt(quantityInput.min, 10) || 1;
    const newValue = Number.isNaN(parsed) ? fallback : parsed;

    quantityInput.value = String(newValue);
    this.dataset.cartQuantity = String(newValue);

    if (quantityScreenReader) {
      quantityScreenReader.textContent = `Current quantity: ${newValue}`;
    }

    quantityInput.dispatchEvent(
      new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine))
    );
  }


  /**
   * Checks the quantity rules are met
   */
#checkQuantityRules = () => {
  const { quantityInput, orderLimitMessage } = this.refs;

  const rawValue = parseInt(quantityInput.value, 10);
  const parsedMin = parseInt(quantityInput.min, 10);
  const parsedMax = parseInt(quantityInput.max, 10);

  const min = Number.isNaN(parsedMin) ? 1 : parsedMin;
  const max = Number.isNaN(parsedMax) ? 999 : parsedMax;
  const newValue = Number.isNaN(rawValue) ? 0 : rawValue;

  const parsedCurrentOrderLimit = parseInt(this.dataset.currentOrderLimit || '', 10);
  const currentOrderLimit = Number.isNaN(parsedCurrentOrderLimit) ? null : parsedCurrentOrderLimit;

  if (newValue < min) quantityInput.value = String(min);
  if (newValue > max) quantityInput.value = String(max);

  if (orderLimitMessage) {
    const parsedCurrentValue = parseInt(quantityInput.value, 10);
    const currentValue = Number.isNaN(parsedCurrentValue) ? 0 : parsedCurrentValue;

    const parsedOrderLimit = parseInt(this.dataset.orderLimit || '', 10);
    const orderLimit = Number.isNaN(parsedOrderLimit) ? null : parsedOrderLimit;

    if (orderLimit !== null) {
      if (currentValue >= orderLimit) {
        this.#showOrderLimitMessage();
      } else {
        this.#hideOrderLimitMessage();
      }
    } else if (currentOrderLimit !== null) {
      if (currentValue >= currentOrderLimit) {
        this.#showOrderLimitMessage();
      } else {
        this.#hideOrderLimitMessage();
      }
    } else {
      this.#hideOrderLimitMessage();
    }
  }

  this.#updateButtonStates();
};

  #showOrderLimitMessage = () => {
    const { orderLimitMessage } = this.refs;
    if (!orderLimitMessage) return;

    orderLimitMessage.classList.remove('order-limit--hidden');
    orderLimitMessage.classList.add('order-limit--visible');
  };

  #hideOrderLimitMessage = () => {
    const { orderLimitMessage } = this.refs;
    if (!orderLimitMessage) return;

    orderLimitMessage.classList.remove('order-limit--visible');
    orderLimitMessage.classList.add('order-limit--hidden');
  };

  /**
   * Updates the disabled state of quantity buttons
   */
  #updateButtonStates = () => {
    const { quantityInput, quantityButtons } = this.refs;
    
    if (!quantityInput || !quantityButtons || quantityButtons.length < 2) return;

    const minusButton = quantityButtons[0];
    const plusButton = quantityButtons[1];

    const currentValue = parseInt(quantityInput.value) || 0;
    const min = parseInt(quantityInput.min) || 1;
    const maxAllowed = parseInt(quantityInput.max) || 999;
    const cartQuantity = this.dataset.cartQuantity;
    const orderLimit = this.dataset.orderLimit;

    if (minusButton) minusButton.disabled = currentValue <= min;
    
    if (plusButton) {
      if (cartQuantity && orderLimit) {
        plusButton.disabled = currentValue >= parseInt(orderLimit);
      } else {
        plusButton.disabled = currentValue >= maxAllowed;
      }
    }
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
